import {
  relationRoleSide,
  type ReuseEntityDefinition,
  type ReuseLinkDefinition,
  roleToRelationMap,
} from '@bizzdesign/modeler/models';
import { getTypeMetadataFromMetaModelTerm } from '@bizzdesign/modeler/utils-canvazz';
import {
  assertIsCombinedRelationRoleName,
  type BaseObjectDto,
  type BaseRelationDto,
} from '@bizzdesign/sdk-knowledgegraph';
import type { ExternalData } from '@bizzdesign/shared/models';
import { convertFromSdkObjectTypeToMetaModelTerm } from './sdk-types.utils';

type SemanticObjectInput = Pick<
  BaseObjectDto,
  'id' | 'name' | 'type' | 'externalId' | 'externalSource'
>;

export type SemanticRelationInput = Pick<
  BaseRelationDto,
  'id' | 'name' | 'roleName' | 'externalId' | 'externalSource'
> & { sourceId: string | undefined; targetId: string | undefined };

export function convertObjectToSemanticalData(
  object: SemanticObjectInput,
): ReuseEntityDefinition {
  const typeName = object.type?.split('.')[1] ?? '';
  const metaModelTerm = convertFromSdkObjectTypeToMetaModelTerm(object.type);
  const hasExternalData = object.externalId && object.externalSource;

  return {
    version: '1.0',
    id: object.id ?? '',
    name: {
      en: object.name ?? '',
    },
    typeName,
    metaModelTerm,
    parentId: 'm0',
    elementKind: 'model',
    metadata: hasExternalData
      ? {
          external: {
            id: object.externalId,
            source: object.externalSource,
          },
        }
      : undefined,
  };
}

export function convertBaseRelationToSemanticalRelationData(
  relation: BaseRelationDto,
): ReuseLinkDefinition {
  const relationData: SemanticRelationInput = {
    id: relation.id,
    name: relation.name,
    roleName: relation.roleName,
    externalSource: relation.externalSource,
    externalId: relation.externalId,
    sourceId: relation.source.id,
    targetId: relation.target.id,
  };
  return converRelationDataToSemanticalRelationData(relationData);
}

export function converRelationDataToSemanticalRelationData(
  relationData: SemanticRelationInput,
): ReuseLinkDefinition {
  assertIsCombinedRelationRoleName(relationData.roleName);
  const metaModelTerm = roleToRelationMap.get(relationData.roleName);
  const relationDirection = relationRoleSide.get(relationData.roleName);
  let sourceId: string | undefined;
  let targetId: string | undefined;
  if (relationDirection === 'none' || relationDirection === 'source') {
    //'none' means the relation doesn't have defined sides, anything goes
    //'source' means the relation side means source is indeed source
    sourceId = relationData.sourceId;
    targetId = relationData.targetId;
  } else if (relationDirection === 'target') {
    //the side of the relation makes the og object the target
    sourceId = relationData.targetId;
    targetId = relationData.sourceId;
  }

  const externalData: ExternalData | undefined =
    relationData.externalId && relationData.externalSource
      ? {
          id: relationData.externalId,
          source: relationData.externalSource,
        }
      : undefined;

  return {
    version: '1.0',
    id: relationData.id ?? '',
    parentId: 'm0',
    sourceId: sourceId ?? '',
    targetId: targetId ?? '',
    typeName: getTypeMetadataFromMetaModelTerm(metaModelTerm).typeName,
    metaModelTerm: metaModelTerm ?? '',
    name: {
      en: relationData.name ?? '',
    },
    elementKind: 'model',
    metadata: externalData
      ? {
          external: { ...externalData },
        }
      : undefined,
  };
}
